import subscribeNotificationPrefs from './subscribeNotificationPrefs';
import NotificationPresets from './NotificationPresets';
import useNotificationContext from './useNotificationContext';
import PastEventChangesSetting from './PastEventChangesSetting';
import CalendarCoverageSetting from "./CalendarCoverageSetting";
// src/components/Notifications/NotificationPrefsPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../Security/firebase-config";
import { NOTIF_TYPES, defaultPrefs, isEnabled, normalizePrefs, serializePrefs, overrideChannel } from "./prefsModel";
import "./NotificationPrefsPanel.css";

// Small helper
const getScopeDocRef = (user, scope) =>
  doc(db, "users", user.uid, "notifPrefs", scope === "global" ? "global" : scope);

export default function NotificationPrefsPanel({
  // Optional: pass team options if you have them on Account page already
  teamOptions = [], // [{id, name}]
  scope: externalScope,
  discordId,                // from /users doc (Account already has it)
  discordEnabled,           // boolean from /users doc
  onNeedConnectDiscord,     // function to nudge open Discord connect flow
  title = "Notification Settings",
}) {
  const user = getAuth().currentUser;
  const [scope, setScope] = useState(externalScope || "global");

  // keep local scope in sync with prop
  useEffect(() => {
    setScope(externalScope || "global");
  }, [externalScope]);

  const [rawPrefs, setPrefs] = useState(defaultPrefs());
  const presetContext=useNotificationContext(user?.uid,scope==='global'?null:scope);
  const prefs=normalizePrefs(rawPrefs,presetContext);
  const [saving, setSaving] = useState(false);
  const [discordOn, setDiscordOn] = useState(!!discordEnabled);

  // keep local switch in sync if the user doc changes elsewhere (e.g. Dropdown)
  useEffect(() => {
    setDiscordOn(!!discordEnabled);
  }, [discordEnabled]);

  // load prefs for current scope
  useEffect(() => {
    if (!user) return;
    return subscribeNotificationPrefs(db,user.uid,scope,setPrefs);
  }, [user, scope]);

  const areAllOn = useMemo(
    () =>
      NOTIF_TYPES.every(({ key }) =>
        ["inApp", "push", "discord"].every((c) => isEnabled(prefs, key, c))
      ),
    [prefs]
  );

  const masterRef = useRef(null);
  useEffect(() => {
    if (!masterRef.current) return;
    const anyOn = NOTIF_TYPES.some(({ key }) =>
      ["inApp", "push", "discord"].some((c) => isEnabled(prefs, key, c))
    );
    masterRef.current.indeterminate = !areAllOn && anyOn;
  }, [prefs, areAllOn]);

  const toggleMaster = async (enabled) => {
    if (!user) return;
    setSaving(true);
    try {
      const ref = getScopeDocRef(user, scope);
      await setDoc(ref,serializePrefs({...prefs,masterEnabled:enabled}));
    } finally {
      setSaving(false);
    }
  };

  const updatePref = async (typeKey, channel, value) => {
    if (!user) return;
    // Discord guard
    if (channel === "discord" && value) {
      if (!discordId) return onNeedConnectDiscord?.();
      if (!discordOn) {
        await updateDoc(doc(db, "users", user.uid), { discordNotificationsEnabled: true });
        setDiscordOn(true);
      }
    }
    setSaving(true);
    try {
      const ref = getScopeDocRef(user, scope);
      await setDoc(ref,serializePrefs(overrideChannel(prefs,typeKey,channel,value,presetContext)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="notif-card">
      <div className="notif-card-header">
        <h3>{title}</h3>
        <div className="notif-actions">
          {saving && <span className="notif-saving">Saving…</span>}
          <label className="notif-master">
            <input
              ref={masterRef}
              type="checkbox"
              checked={!!prefs.masterEnabled}
              onChange={(e) => toggleMaster(e.target.checked)}
            />
            <span>{prefs.masterEnabled ? "On" : "Off"}</span>
          </label>
        </div>
      </div>

      <div className="notif-scope-row">
        <label htmlFor="notif-scope">Apply settings for:</label>
        <select
            id="notif-scope"
            value={scope}
            onChange={async (e) => {
                const val = e.target.value;
                setScope(val);
                if (user) {
                    await updateDoc(doc(db, "users", user.uid), {
                        activeTeamId: val === "global" ? null : val,
                    });
                }
            }}
        >
          <option value="global">Global (account-wide)</option>
          {teamOptions.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      <NotificationPresets prefs={prefs} context={presetContext} scope={scope} onChange={async next=>{await setDoc(getScopeDocRef(user,scope),serializePrefs(next));setPrefs(next);}}/>
      <CalendarCoverageSetting prefs={prefs} onChange={async value=>{const user=getAuth().currentUser;if(!user)return;await setDoc(doc(db,'users',user.uid,'notifPrefs',scope),serializePrefs({...prefs,followTeamEvents:value}));}}/>
<PastEventChangesSetting prefs={prefs} onChange={async value=>{const user=getAuth().currentUser;if(!user)return;await setDoc(doc(db,'users',user.uid,'notifPrefs',scope),serializePrefs({...prefs,includePastEventChanges:value}));}}/>
<div className="notif-legend">
        <span>Type</span>
        <span>In-app</span>
        <span>Push</span>
        <span>Discord</span>
      </div>

      {NOTIF_TYPES.map(({ key, label, description, noDiscord, group }, index) => (
        <div key={key} className="notif-row" role="group" aria-label={`${label} preferences`}>
          {(index===0 || NOTIF_TYPES[index-1].group!==group) && <strong className="notification-group-title">{group}</strong>}
          <span className="notif-type">
            <div className="notif-type__label">{label}</div>
            {description ? <div className="notif-type__desc">{description}</div> : null}
          </span>

          <label className="notif-check" data-channel="In-app">
            <input
              type="checkbox"
              checked={isEnabled(prefs, key, "inApp")}
              onChange={(e) => updatePref(key, "inApp", e.target.checked)}
            />
          </label>

          <label className="notif-check" data-channel="Push">
            <input
              type="checkbox"
              checked={isEnabled(prefs, key, "push")}
              onChange={(e) => updatePref(key, "push", e.target.checked)}
            />
          </label>

          <label className="notif-check" data-channel="Discord">
            <input
              type="checkbox"
              checked={isEnabled(prefs, key, "discord") && discordOn}
              disabled={noDiscord}
              onChange={(e) => updatePref(key, "discord", e.target.checked)}
            />
          </label>

        </div>
      ))}

      {!discordId && (
        <div className="notif-discord-hint">
          Connect Discord on this page to enable DM notifications.
        </div>
      )}
    </div>
  );
}
